package sarka.design.skoria.network

/**
 * Single source of truth for server endpoints across every platform.
 *
 * Previously each platform (android/jvm/js) built its own URLs, which let them
 * drift apart: web was moved to HTTP/9090 while JVM and Android were left
 * pointing at an HTTPS/9443 endpoint that no longer existed. Keeping the
 * decision here means that can't happen again.
 *
 * To change environment, change [active] and nothing else.
 */
object ServerConfig {

    data class Endpoint(
        val host: String,
        val port: Int,
        /** true = https/wss with normal certificate validation, false = cleartext http/ws */
        val secure: Boolean,
    ) {
        /** Omit the port when it's the scheme default, so URLs stay canonical. */
        private val portSuffix: String
            get() = when {
                secure && port == 443 -> ""
                !secure && port == 80 -> ""
                else -> ":$port"
            }

        val apiUrl: String
            get() = "${if (secure) "https" else "http"}://$host$portSuffix"

        private val wsScheme: String
            get() = if (secure) "wss" else "ws"

        /** Random-stranger matchmaking socket. */
        val wsUrl: String
            get() = "$wsScheme://$host$portSuffix/ws"

        /** Direct-message delivery socket. */
        val dmUrl: String
            get() = "$wsScheme://$host$portSuffix/dm"
    }

    /**
     * Real certificate, terminated by the reverse proxy in front of Ktor.
     * Works on web, Android and desktop with no custom trust configuration.
     */
    val Production = Endpoint(host = "api.skoria.app", port = 443, secure = true)

    /** Ktor reached directly over the LAN. Cleartext: passwords and JWTs are readable. */
    val LanDev = Endpoint(host = "192.168.0.12", port = 9090, secure = false)

    /** Android emulator reaching the host machine. Cleartext. */
    val EmulatorDev = Endpoint(host = "10.0.2.2", port = 9090, secure = false)

    /** Server running on the same machine as the client. Cleartext. */
    val LocalDev = Endpoint(host = "localhost", port = 9090, secure = false)

    /**
     * The endpoint every client uses.
     *
     * The cleartext endpoints above exist for local debugging only. They are
     * not a fallback: production traffic goes over TLS to [Production] and
     * there is no automatic downgrade.
     */
    val active: Endpoint = Production
}
